<template>
  <section class="edit-ticket-content__scrollable">
    <AccordionCustomTickets
      v-if="store.getters.hasFileReaderAccess"
      :accordionText="t('general__data__files')"
      :active="accordionAttachments1Active"
      class="edit-ticket-content__accordion"
      @onClicked="accordionAttachments1Active = !accordionAttachments1Active"
    />
    <article
      v-show="accordionAttachments1Active && store.getters.hasFileReaderAccess"
      class="edit-ticket-content__article"
    >
      <OverlayCard
        v-if="showConfirmDeleteFile"
        class="overlay-confirm-delete"
        @closeOverlay="showConfirmDeleteFile = false"
      >
        <ConfirmDeleteAttachmentCard
          :buttonText="t('general__text__delete') + ' ' + t('general__data__file(s)').toLowerCase()"
          @closeOverlay="showConfirmDeleteFile = false"
          @confirmDelete="deleteFile()"
        />
      </OverlayCard>
      <div class="edit-ticket-content__article__table-container">
        <PulseLoader :loading="isLoadingFiles" class="spinner" color="#1d234f" />
        <TableFilesMini
          v-if="!isLoadingFiles"
          class="edit-ticket-content__article__table-container__table"
          :fields="filesFields"
          :items="filesList?.length ? filesList : []"
          @archiveFile="archiveFile"
          @deleteFile="showConfirmDeleteFile = true"
          @downloadFile="downloadFile"
        />
      </div>
      <ButtonText
        v-if="
          store.getters.hasErrandEditorAccess &&
          store.getters.hasFileCreatorAccess &&
          selectedTicket?.active
        "
        class="edit-ticket-content__article__button"
        :buttonText="t('general__data__file-upload-a')"
        :iconLeft="iconUploadSmall"
        :size="'small'"
        @onClicked="showModalUploadFiles"
      />
    </article>
    <DividerHorizontal
      v-if="store.getters.hasFileReaderAccess && store.getters.hasLinkReaderAccess"
    />
    <AccordionCustomTickets
      v-if="store.getters.hasLinkReaderAccess"
      :accordionText="t('general__data__links')"
      :active="accordionAttachments2Active"
      class="edit-ticket-content__accordion"
      @onClicked="accordionAttachments2Active = !accordionAttachments2Active"
    />
    <article
      v-show="accordionAttachments2Active && store.getters.hasLinkReaderAccess"
      class="edit-ticket-content__article"
    >
      <OverlayCard
        v-if="showConfirmDeleteLink"
        class="overlay-confirm-delete"
        @closeOverlay="showConfirmDeleteLink = false"
      >
        <ConfirmDeleteAttachmentCard
          :buttonText="t('general__text__delete') + ' ' + t('general__data__link(s)').toLowerCase()"
          @closeOverlay="showConfirmDeleteLink = false"
          @confirmDelete="deleteLink()"
        />
      </OverlayCard>
      <div class="edit-ticket-content__article__table-container">
        <PulseLoader :loading="isLoadingLinks" class="spinner" color="#1d234f" />
        <TableLinksMini
          v-if="!isLoadingLinks"
          class="edit-ticket-content__article__table-container__table"
          :fields="linksFields"
          :items="linksList?.length ? linksList : []"
          @archiveLink="archiveLink"
          @deleteLink="showConfirmDeleteLink = true"
        />
      </div>
      <ButtonText
        v-if="
          store.getters.hasErrandEditorAccess &&
          store.getters.hasLinkCreatorAccess &&
          selectedTicket?.active
        "
        class="edit-ticket-content__article__button"
        :buttonText="t('general__data__link-add-new')"
        :iconLeft="iconPlusSmall"
        :size="'small'"
        @onClicked="showModalCreateLink"
      />
    </article>
  </section>
</template>
<script lang="ts" setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { useI18n } from 'vue-i18n'
import { useGeneralStore } from '@/pinia'
import { useStore } from 'vuex'
import {
  archiveAttachments,
  Attachment,
  deleteAttachments,
  getAttachmentsPage
} from '@/services/attachments.service'
import { getAttachmentFileBlob } from '@/services/files.service'
import { orderAlphabetical } from '@/utils/sort'

import AccordionCustomTickets from '@/components/features/tickets/AccordionCustomTickets.vue'
import ButtonText from '@/design-system/atoms/buttons/ButtonText.vue'
import ConfirmDeleteAttachmentCard from '@/components/features/attachments/ConfirmDeleteAttachmentCard.vue'
import DividerHorizontal from '@/design-system/atoms/DividerHorizontal.vue'
import OverlayCard from '@/design-system/molecules/OverlayCard.vue'
import PulseLoader from 'vue-spinner/src/PulseLoader.vue'
import TableFilesMini from '@/components/features/attachments/TableFilesMini.vue'
import TableLinksMini from '@/components/features/attachments/TableLinksMini.vue'
import iconUploadSmall from '@/design-system/assets/icons/Icon-Upload-Small.svg'
import iconPlusSmall from '@/design-system/assets/icons/Icon-Plus-Small.svg'

const { t } = useI18n()
const piniaStore = useGeneralStore()
const store = useStore()

const accordionAttachments1Active = ref(true)
const accordionAttachments2Active = ref(true)
const isLoadingFiles = ref(false)
const isLoadingLinks = ref(false)
const showConfirmDeleteFile = ref(false)
const showConfirmDeleteLink = ref(false)

const { filesList, filesUploaded, linksList, linksUpdated, selectedTicket } =
  storeToRefs(piniaStore)

const filesFields = computed(() => {
  const fields = [
    {
      key: 'name',
      name: filesList.value?.length
        ? filesList.value.length + ' ' + t('general__label__files').toLocaleLowerCase()
        : ''
    }
  ]
  return fields
})
const linksFields = computed(() => {
  const fields = [
    {
      key: 'name',
      name: linksList.value?.length
        ? linksList.value.length + ' ' + t('general__data__links').toLocaleLowerCase()
        : ''
    }
  ]
  return fields
})

async function archiveFile() {
  if (!piniaStore.selectedFile) return
  try {
    const id = piniaStore.selectedFile.attachmentId
    if (!id) return
    const response = await archiveAttachments([id])
    if (response?.data.success) {
      // remove file from filesList in store
      const filesArray = piniaStore.filesList.filter((file) => file.attachmentId !== id)
      piniaStore.setFilesList(filesArray)
      piniaStore.setSelectedFile(null)
    }
    if (!response?.data.success) {
      showArchiveFileError()
    }
  } catch (e) {
    showArchiveFileError()
    console.error(e)
  }
}
async function archiveLink() {
  if (!piniaStore.selectedLink) return
  try {
    const id = piniaStore.selectedLink.attachmentId
    if (!id) return
    const response = await archiveAttachments([id])
    if (response?.data.success) {
      // remove link from linksList in store
      const linksArray = linksList.value.filter((link) => link.attachmentId !== id)
      piniaStore.setLinksList(linksArray)
      piniaStore.setSelectedLink(null)
    }
    if (!response?.data.success) {
      showArchiveLinkError()
    }
  } catch (e) {
    showArchiveLinkError()
    console.error(e)
  }
}
async function deleteFile() {
  if (!piniaStore.selectedFile) return
  try {
    const entityId = piniaStore.selectedFile.entityId
    const id = piniaStore.selectedFile.attachmentId
    if (!entityId || !id) return
    const response = await deleteAttachments([piniaStore.selectedFile.attachmentId])
    if (response?.data.success) {
      // remove file from filesList in store
      const filesArray = piniaStore.filesList.filter((file) => file.attachmentId !== id)
      piniaStore.setFilesList(filesArray)
      piniaStore.setSelectedFile(null)
    }
    if (!response?.data.success) {
      showDeleteFileError()
    }
  } catch (e) {
    showDeleteFileError()
    console.error(e)
  }
}
async function deleteLink() {
  if (!piniaStore.selectedLink) return
  try {
    const id = piniaStore.selectedLink.attachmentId
    if (!id) return
    const response = await deleteAttachments([piniaStore.selectedLink.attachmentId])
    if (response?.data.success) {
      // remove link from linksList in store
      const linksArray = linksList.value.filter((link) => link.attachmentId !== id)
      piniaStore.setLinksList(linksArray)
      piniaStore.setSelectedLink(null)
    }
    if (!response?.data.success) {
      showDeleteLinkError()
    }
  } catch (e) {
    showDeleteLinkError()
    console.error(e)
  }
}
async function downloadFile() {
  if (
    !piniaStore.selectedFile ||
    !piniaStore.selectedFile.realmId ||
    !piniaStore.selectedFile.storageName
  )
    return
  try {
    const res = await getAttachmentFileBlob({
      realmId: piniaStore.selectedFile.realmId,
      storageName: piniaStore.selectedFile.storageName
    })
    if (!res) return setDownloadFailedMessage()
    const fileUrl = window.URL.createObjectURL(new Blob([res.data]))
    const fileLink = document.createElement('a')
    fileLink.href = fileUrl
    fileLink.setAttribute('download', piniaStore.selectedFile.name)
    document.body.appendChild(fileLink)
    fileLink.click()
  } catch (e) {
    setDownloadFailedMessage()
    console.error(e)
  }
}
async function getFilesList(ticketId: string) {
  isLoadingFiles.value = true
  const filesArray: Attachment[] = []
  const filesPage = await getAttachmentsPage(
    ticketId,
    ['FILE'],
    0,
    100, // Might need adjustment in the future, but currently we don't expect large amounts of files on tickets
    'nameLower,asc',
    null,
    false
  )
  filesPage?.content.forEach((item) => {
    filesArray.push(item)
  })
  piniaStore.setFilesList(filesArray)
  isLoadingFiles.value = false
}
async function getLinksList(ticketId: string) {
  isLoadingLinks.value = true
  const linksArray: Attachment[] = []
  const linksPage = await getAttachmentsPage(ticketId, ['LINK'], 0, 100, 'name,asc', null, false)
  linksPage?.content.forEach((item) => {
    linksArray.push(item)
  })
  linksArray.sort(orderAlphabetical)
  piniaStore.setLinksList(linksArray)
  isLoadingLinks.value = false
}
function setDownloadFailedMessage() {
  store.commit('setInfoMessage', {
    type: 'error',
    message: `${t('general__data__file-download-failed')}, ${t(
      'general__text__retry-later-again'
    ).toLowerCase()}`
  })
}
function showArchiveFileError() {
  store.commit('setInfoMessage', {
    type: 'error',
    message: `${t('general__data__file-archive-failed')}, ${t(
      'general__text__retry-later-again'
    ).toLowerCase()}`
  })
}
function showArchiveLinkError() {
  store.commit('setInfoMessage', {
    type: 'error',
    message: `${t('general__data__link-archive-failed')}, ${t(
      'general__text__retry-later-again'
    ).toLowerCase()}`
  })
}
function showDeleteFileError() {
  store.commit('setInfoMessage', {
    type: 'error',
    message: `${t('general__data__file-delete-failed')}, ${t(
      'general__text__retry-later-again'
    ).toLowerCase()}`
  })
}
function showDeleteLinkError() {
  store.commit('setInfoMessage', {
    type: 'error',
    message: `${t('general__data__link-delete-failed')}, ${t(
      'general__text__retry-later-again'
    ).toLowerCase()}`
  })
}
function showModalCreateLink() {
  piniaStore.setCurrentOverlayModalOnModalPinia('editTicketLink')
  piniaStore.setShowOverlayModalOnModalPinia(true)
}
function showModalUploadFiles() {
  piniaStore.setCurrentOverlayModalOnModalPinia('uploadTicketFiles')
  piniaStore.setShowOverlayModalOnModalPinia(true)
}

watch(
  filesUploaded,
  (newFilesArray, oldFilesArray) => {
    if (newFilesArray.length && newFilesArray.length > oldFilesArray.length) {
      piniaStore.setFilesList([])
      if (selectedTicket.value) {
        getFilesList(selectedTicket.value.id)
      }
    }
  },
  { deep: true }
)
watch(
  linksUpdated,
  (newLinksArray, oldLinksArray) => {
    if (newLinksArray.length && newLinksArray.length > oldLinksArray.length) {
      piniaStore.setLinksList([])
      if (selectedTicket.value) {
        getLinksList(selectedTicket.value.id)
      }
    }
  },
  { deep: true }
)
onBeforeUnmount(() => {
  piniaStore.setSelectedFile(null)
  piniaStore.setSelectedLink(null)
  piniaStore.setFilesList([])
  piniaStore.setLinksList([])
})
onMounted(() => {
  if (selectedTicket.value) {
    if (store.getters.hasFileCreatorAccess) getFilesList(selectedTicket.value.id)
    if (store.getters.hasLinkCreatorAccess) getLinksList(selectedTicket.value.id)
  }
})
</script>
<style scoped lang="scss">
@use '@/design-system/design-tokens/variables.scss';
@use '@/design-system/design-tokens/font-mixins.scss';
@use '@/design-system/design-tokens/mixins.scss';

.edit-ticket-content {
  &__scrollable {
    overflow-y: auto;
    overflow-x: hidden;
    @include mixins.scrollbar-vertical;
  }
  &__accordion {
    width: 100%;
  }
  &__article {
    @include font-mixins.desktop-bold-paragraph;
    color: variables.$color-greyscale-content;
    padding: 24px;
    &__button {
      color: variables.$color-primary;
    }
    &__table-container {
      display: contents;
      &__table {
        margin-bottom: 8px;
        max-height: 200px;
        padding: 0;
      }
    }
  }
}
.overlay-confirm-delete {
  left: 0;
  top: 0;
  z-index: calc(#{variables.$z-index-overlay} + 1);
}
.spinner {
  display: flex;
  height: 100%;
  align-items: center;
  justify-content: center;
  padding-bottom: 40px;
}
</style>
